import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Reveal, { MaskedLine } from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import { SERVICES, PROCESS } from "@/data/content";

export default function Services() {
  return (
    <div data-testid="services-page">
      <header className="pt-40 pb-16 md:pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <p className="flex items-center gap-4 text-xs uppercase tracking-[0.35em] text-cobalt font-medium">
              <span className="h-px w-10 bg-cobalt/60" /> Services
            </p>
          </Reveal>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink leading-[1.08]" data-testid="services-heading">
            <MaskedLine delay={0.1}>Practical AI for</MaskedLine>
            <MaskedLine delay={0.25}>
              <em className="text-cobalt">your</em> business.
            </MaskedLine>
          </h1>
          <Reveal delay={0.4}>
            <p className="mt-8 max-w-2xl text-base md:text-lg text-ink/60 leading-relaxed">
              Five straightforward services that take you from &ldquo;where do we start?&rdquo; to AI
              tools your team uses every day. No jargon, no lock-in, no hype.
            </p>
          </Reveal>
        </div>
      </header>

      <section className="border-t border-ink/10" data-testid="services-list">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          {SERVICES.map((s, i) => (
            <Reveal key={s.num} delay={0.03 * i}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 py-14 border-b border-ink/10" data-testid={`service-row-${s.num}`}>
                <div className="md:col-span-1">
                  <span className="font-display text-4xl text-cobalt/70">{s.num}</span>
                </div>
                <div className="md:col-span-5">
                  <h2 className="font-display text-2xl md:text-3xl tracking-tight text-ink">{s.title}</h2>
                  <p className="mt-4 text-ink/60 leading-relaxed">{s.desc}</p>
                </div>
                <div className="md:col-span-5 md:col-start-8">
                  <p className="text-xs uppercase tracking-[0.3em] text-ink/40 mb-4">Deliverables</p>
                  <ul className="space-y-2.5">
                    {s.deliverables.map((d) => (
                      <li key={d} className="flex items-center gap-3 text-ink/75">
                        <span className="h-1 w-1 rounded-full bg-cobalt" /> {d}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white border-b border-ink/10" data-testid="services-spotlight">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-8">
            <SectionHeading eyebrow="How we work" title="Proven tools, set up properly." />
            <Reveal delay={0.15}>
              <p className="mt-6 text-ink/60 leading-relaxed max-w-2xl">
                We don&rsquo;t sell our own software. We pick reliable, off-the-shelf AI tools, configure
                them for your business, connect them to the systems you already use, and train your
                team to run them with confidence.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <Link
                to="/contact"
                data-testid="services-research-link"
                className="mt-8 inline-flex items-center gap-2 text-sm uppercase tracking-[0.25em] text-cobalt hover:gap-4 transition-[gap] duration-300"
              >
                Ask us anything <ArrowUpRight size={16} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeading eyebrow="Method" title="How an engagement unfolds." />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-px bg-ink/10 border border-ink/10">
            {PROCESS.map((p, i) => (
              <Reveal key={p.num} delay={i * 0.07} className="bg-paper">
                <div className="p-8 md:p-10 h-full" data-testid={`process-step-${p.num}`}>
                  <span className="font-display text-4xl text-cobalt/70">{p.num}</span>
                  <h3 className="mt-6 font-display text-2xl text-ink">{p.title}</h3>
                  <p className="mt-3 text-sm text-ink/60 leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-28 border-t border-ink/10" data-testid="services-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-wrap items-center justify-between gap-8">
          <Reveal>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight text-ink">
              Not sure which practice<br />fits? <em className="text-cobalt">That&rsquo;s the first meeting.</em>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/contact"
              data-testid="services-cta-button"
              className="inline-flex items-center gap-3 rounded-full bg-ink text-paper px-8 py-4 text-sm tracking-wide transition-all duration-200 hover:bg-cobalt hover:scale-[0.98]"
            >
              Book a discovery call <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
